import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import anime from 'animejs/lib/anime.es.js';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight } from 'lucide-react';

// Scrolling text animation component
function ScrollingText({ text, speed = 40 }) {
  return (
    <div style={{
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      padding: '48px 0',
      background: 'rgba(10,22,40,0.3)',
      borderTop: '1px solid rgba(240,244,248,0.05)',
      borderBottom: '1px solid rgba(240,244,248,0.05)',
    }}>
      <div 
        style={{
          display: 'inline-block',
          animation: `scroll-left ${speed}s linear infinite`,
        }}
      >
        {[...Array(12)].map((_, i) => (
          <span 
            key={i}
            style={{
              fontFamily: 'Syne, sans-serif',
              fontSize: 'clamp(16px, 3vw, 22px)',
              fontWeight: 700,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
              color: 'rgba(240,244,248,0.12)',
              marginRight: '80px',
            }}
          >
            {text}
          </span>
        ))}
      </div>
      <style>
        {`
          @keyframes scroll-left {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}
      </style>
    </div>
  );
}

// Partner/Collaborator card component
function CollabCard({ name, description, link, linkLabel }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div 
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '28px',
        background: hovered ? 'rgba(240,244,248,0.03)' : 'rgba(10,22,40,0.4)',
        border: '1px solid rgba(240,244,248,0.06)',
        borderRadius: '4px',
        transition: 'all 0.4s ease',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
      }}
    >
      <h4 style={{
        fontFamily: 'Syne, sans-serif',
        fontSize: '16px',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#F0F4F8',
        marginBottom: '12px',
      }}>
        {name}
      </h4>
      <p style={{
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '13px',
        color: 'var(--vvs-muted)',
        lineHeight: 1.7,
        marginBottom: link ? '16px' : '0',
      }}>
        {description}
      </p>
      {link && (
        <a 
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: '12px',
            letterSpacing: '0.1em',
            color: 'var(--vvs-cyan)',
            textDecoration: 'none',
            transition: 'gap 0.3s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.gap = '10px'}
          onMouseLeave={e => e.currentTarget.style.gap = '6px'}
        >
          {linkLabel} <ArrowRight size={14} />
        </a>
      )}
    </div>
  );
}

export default function VisionPage() {
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Section refs
  const behindRef = useScrollReveal({ delay: 100 });
  const ownWaveRef = useScrollReveal({ delay: 100 });
  const whatWeDoRef = useScrollReveal({ delay: 150 });
  const visionRef = useScrollReveal({ delay: 100 });
  const fairflowRef = useScrollReveal({ delay: 100 });
  const quoteRef = useScrollReveal({ delay: 200 });
  const collabRef = useScrollReveal({ delay: 100 });
  const contactRef = useScrollReveal({ delay: 150 });

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    document.title = 'Vision // VVS';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroRef.current) return;

    anime.timeline({ easing: 'easeOutExpo' })
      .add({ targets: '.vis-own', translateY: [40, 0], opacity: [0, 1], duration: 1200 }, 200)
      .add({ targets: '.vis-intro span', translateY: [20, 0], opacity: [0, 1], duration: 700, delay: anime.stagger(120) }, '-=700');
  }, []);

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '64px', overflowX: 'hidden' }}>

      {/* ═══════════════════════════════════════════════════════════════════════
          HERO — OWN YOUR WAVE
      ═══════════════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} style={{
        minHeight: isMobile ? 'auto' : '75vh', 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        padding: isMobile ? '100px 20px 80px' : '140px 48px 100px',
        textAlign: 'center',
        maxWidth: '1000px',
        margin: '0 auto',
      }} data-testid="vision-hero">
        <h1 className="vis-own neon-heading" data-testid="vision-own-wave" style={{
          fontSize: isMobile ? '48px' : 'clamp(64px, 10vw, 120px)',
          lineHeight: 0.9, 
          letterSpacing: '-0.02em', 
          textTransform: 'uppercase',
          margin: '0 0 48px', 
          opacity: 0,
        }}>
          OWN YOUR WAVE
        </h1>

        <div className="vis-intro" style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
          <span style={{
            display: 'block',
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '16px' : 'clamp(18px, 2.5vw, 24px)',
            color: 'var(--vvs-muted)',
            letterSpacing: '0.1em',
            opacity: 0,
          }}>
            A wave is never still.
          </span>
          <span style={{
            display: 'block',
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            fontSize: isMobile ? '18px' : 'clamp(20px, 3vw, 28px)',
            color: '#F0F4F8',
            letterSpacing: '0.02em',
            opacity: 0,
            marginTop: '8px',
          }}>
            {t('vision.intro4')} {t('vision.intro5')}
          </span>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          BEHIND THE LABEL — Narrative anchor
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{ 
        padding: isMobile ? '80px 20px' : '120px 48px',
        background: 'rgba(10,22,40,0.3)',
        borderTop: '1px solid rgba(240,244,248,0.04)',
        borderBottom: '1px solid rgba(240,244,248,0.04)',
      }}>
        <div ref={behindRef} className="reveal-el" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <p className="caption" style={{ marginBottom: '20px' }}>Behind the label</p>
          
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? '28px' : 'clamp(36px, 5vw, 52px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '48px',
          }}>
            {t('vision.behind_title')}
          </h2>
          
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '15px' : '17px',
            color: 'var(--vvs-steel)',
            lineHeight: 1.9,
            marginBottom: '40px',
          }}>
            Vague Vision Sound was founded in 2025 by three people who believed the underground deserved better than survival mode, and who decided that building something lasting meant building it right.
          </p>

          {/* Founders */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '20px',
            marginBottom: '48px',
          }}>
            {[
              { name: 'fromnow', role: 'producer, mixing & mastering engineer, artist.' },
              { name: 'Nina', role: 'manager, brand strategist, co-founder of Musicpreneurs.' },
              { name: 'Gaston', role: 'graphic designer, visual architect of the VVS identity.' },
            ].map(founder => (
              <div key={founder.name} style={{
                padding: '24px',
                background: 'rgba(10,22,40,0.5)',
                border: '1px solid rgba(240,244,248,0.08)',
                borderRadius: '4px',
              }}>
                <p style={{
                  fontFamily: 'Syne, sans-serif',
                  fontWeight: 700,
                  fontSize: '15px',
                  letterSpacing: '0.1em',
                  color: 'var(--vvs-cyan)',
                  marginBottom: '10px',
                }}>
                  {founder.name}
                </p>
                <p style={{
                  fontFamily: 'Space Grotesk, sans-serif',
                  fontSize: '14px',
                  color: 'var(--vvs-muted)',
                  lineHeight: 1.6,
                }}>
                  {founder.role}
                </p>
              </div>
            ))}
          </div>

          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '15px' : '16px',
            color: 'var(--vvs-muted)',
            lineHeight: 1.9,
            marginBottom: '24px',
          }}>
            Three disciplines. One vision. A space where sound meets intention.
          </p>

          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '15px' : '16px',
            color: 'var(--vvs-muted)',
            lineHeight: 1.9,
            marginBottom: '24px',
          }}>
            VVS is a creative infrastructure // built to support artists at every stage of their journey, from the first demo to the full live experience. We release music, produce events, build visual identities, and develop the tools artists need to exist on their own terms.
          </p>

          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '15px' : '16px',
            color: 'var(--vvs-muted)',
            lineHeight: 1.9,
          }}>
            Every release we put out carries a concept. Every event we build carries a world. Every artist we work with carries a story worth telling.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          OWN YOUR WAVE — The Philosophy
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{ 
        padding: isMobile ? '80px 20px' : '120px 48px',
      }}>
        <div ref={ownWaveRef} className="reveal-el" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? '32px' : 'clamp(40px, 6vw, 64px)',
            lineHeight: 1.0,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '48px',
          }}>
            OWN YOUR WAVE
          </h2>
          
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '16px' : '18px',
            color: 'var(--vvs-steel)',
            lineHeight: 1.9,
            marginBottom: '32px',
          }}>
            Vague Vision Sound is more than a label, a brand or an agency. It is a space for artists who dare to elevate beyond their limits, explore every facet of their identity, and leave a mark on culture.
          </p>

          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '15px' : '16px',
            color: 'var(--vvs-muted)',
            lineHeight: 1.9,
            marginBottom: '32px',
          }}>
            Vague means motion. A force in constant evolution, shaping everything in its path. Just like sound. Just like vision. Just like those who create without fear.
          </p>

          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '14px' : '15px',
            color: 'rgba(148,163,184,0.7)',
            lineHeight: 2,
            fontStyle: 'italic',
            marginBottom: '32px',
          }}>
            A wave is never still. It rises from unseen forces // energy, gravity, momentum. No wave is ever the same, yet each carries the pulse of its origin. Some crash with intensity. Some stretch endlessly across the horizon. Some are born from chaos. Others flow with precision. But all exist to move. To transform. To redefine space.
          </p>

          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '15px' : '16px',
            color: 'var(--vvs-muted)',
            lineHeight: 1.9,
          }}>
            We empower creators through innovation and collaboration. From production to performance, we design immersive experiences that resonate with something deeper.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          WHAT WE DO — 4 Cards
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{ 
        padding: isMobile ? '80px 20px' : '120px 48px',
        borderTop: '1px solid rgba(240,244,248,0.04)',
      }}>
        <div ref={whatWeDoRef} className="reveal-el" style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? '28px' : 'clamp(36px, 5vw, 52px)',
            lineHeight: 1.0,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '56px',
          }}>
            WHAT WE DO
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '28px',
          }}>
            {[
              {
                title: 'RELEASES',
                desc: 'Music released under VVS is curated for depth, concept and lasting impact. Not for the algorithm. Every release is a world // built with intention, released with care.',
              },
              {
                title: 'EVENTS',
                desc: 'From underground venues to the Brussels Planetarium, we produce immersive experiences that push the boundaries of what a night out can be. Sound, visuals, space // treated as one.',
              },
              {
                title: 'ARTIST DEVELOPMENT',
                desc: 'We support artists across the full spectrum // production, mixing & mastering, booking, brand identity, communications. We don\'t just release music. We build careers.',
              },
              {
                title: 'MIXING & MASTERING',
                desc: 'Professional audio services for artists with a real sonic signature. All genres. Handled by fromnow from his own setup and through Temple Studios.',
              },
            ].map(item => (
              <div key={item.title} style={{
                padding: '32px',
                background: 'rgba(10,22,40,0.4)',
                border: '1px solid rgba(240,244,248,0.06)',
                borderRadius: '4px',
              }}>
                <h3 style={{
                  fontFamily: 'Syne, sans-serif',
                  fontWeight: 700,
                  fontSize: isMobile ? '16px' : '18px',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: '#F0F4F8',
                  marginBottom: '20px',
                }}>
                  {item.title}
                </h3>
                <p style={{
                  fontFamily: 'Space Grotesk, sans-serif',
                  fontSize: '14px',
                  color: 'var(--vvs-muted)',
                  lineHeight: 1.8,
                }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          OUR VISION — Sound First, Your Art Has No Limits
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{ 
        padding: isMobile ? '80px 20px' : '120px 48px',
        borderTop: '1px solid rgba(240,244,248,0.04)',
      }}>
        <div ref={visionRef} className="reveal-el" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? '28px' : 'clamp(36px, 5vw, 52px)',
            lineHeight: 1.0,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '56px',
          }}>
            OUR VISION
          </h2>
          
          {/* Sound First */}
          <div style={{ marginBottom: '56px' }}>
            <h3 style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 700,
              fontSize: isMobile ? '20px' : '26px',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: '#F0F4F8',
              marginBottom: '20px',
            }}>
              SOUND FIRST
            </h3>
            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: isMobile ? '15px' : '16px',
              color: 'var(--vvs-muted)',
              lineHeight: 1.9,
            }}>
              Every decision starts with the music. We release what moves us, not what performs. Quality, depth, and emotional honesty over everything.
            </p>
          </div>

          {/* Your Art Has No Limits */}
          <div>
            <h3 style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 700,
              fontSize: isMobile ? '20px' : '26px',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              color: '#F0F4F8',
              marginBottom: '20px',
            }}>
              YOUR ART HAS NO LIMITS. YOUR STRUCTURE SHOULD.
            </h3>
            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: isMobile ? '15px' : '16px',
              color: 'var(--vvs-muted)',
              lineHeight: 1.9,
              marginBottom: '20px',
            }}>
              We believe art should never be caged. No genre. No box. No compromise on the vision. But we've seen too many artists lose everything // not because they lacked talent, but because they lacked structure. A contract they didn't understand. A project that disappeared overnight. Rights they didn't know they had.
            </p>
            <p style={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: isMobile ? '15px' : '16px',
              color: 'var(--vvs-muted)',
              lineHeight: 1.9,
              marginBottom: '24px',
            }}>
              That's why everything we build at VVS // from our releases to FairFlow // exists to give artists the framework to last. Not to limit them. To protect them.
            </p>
            <p style={{
              fontFamily: 'Syne, sans-serif',
              fontWeight: 700,
              fontSize: isMobile ? '17px' : '20px',
              color: '#F0F4F8',
              letterSpacing: '0.04em',
            }}>
              Structure protects Culture. That's how it lasts.
            </p>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          FAIRFLOW — Dedicated Section with distinct background
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{
        padding: isMobile ? '80px 20px' : '120px 48px',
        background: 'linear-gradient(180deg, rgba(138,92,246,0.08) 0%, rgba(59,130,246,0.06) 100%)',
        borderTop: '1px solid rgba(138,92,246,0.15)',
        borderBottom: '1px solid rgba(138,92,246,0.15)',
      }}>
        <div ref={fairflowRef} className="reveal-el" style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? '36px' : 'clamp(48px, 7vw, 72px)',
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            background: 'linear-gradient(135deg, rgba(138,92,246,1), rgba(59,130,246,1))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '32px',
          }}>
            FAIRFLOW
          </h2>
          
          <p style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 600,
            fontSize: isMobile ? '18px' : '22px',
            color: '#F0F4F8',
            marginBottom: '32px',
            letterSpacing: '0.02em',
          }}>
            We didn't just talk about transparency // we built it.
          </p>
          
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '15px' : '16px',
            color: 'var(--vvs-muted)',
            lineHeight: 1.9,
            marginBottom: '24px',
          }}>
            FairFlow is a contribution and transparency infrastructure built for artists, by artists. Co-created by fromnow and developer Julien, designed for labels, collectives, and any cultural organization that believes the underground deserves better tools.
          </p>
          
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '14px' : '15px',
            color: 'var(--vvs-steel)',
            fontStyle: 'italic',
            marginBottom: '36px',
          }}>
            Because structure shouldn't be the enemy of art. It should be its armor.
          </p>
          
          <a 
            href="https://fairflow.be"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              fontFamily: 'Space Grotesk, sans-serif',
              fontSize: '14px',
              fontWeight: 500,
              letterSpacing: '0.12em',
              color: '#F0F4F8',
              textDecoration: 'none',
              padding: '16px 32px',
              background: 'linear-gradient(135deg, rgba(138,92,246,0.7), rgba(59,130,246,0.7))',
              borderRadius: '4px',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-3px)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(138,92,246,0.3)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            fairflow.be <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          QUOTE — fromnow citation
      ═══════════════════════════════════════════════════════════════════════ */}
      <section ref={quoteRef} className="reveal-el" style={{
        padding: isMobile ? '100px 20px' : '140px 48px',
        background: 'linear-gradient(180deg, rgba(10,22,40,0.7) 0%, rgba(5,10,20,0.9) 100%)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '850px', margin: '0 auto' }}>
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '20px' : 'clamp(24px, 3.5vw, 32px)',
            fontStyle: 'italic',
            color: 'rgba(240,244,248,0.9)',
            lineHeight: 1.7,
            marginBottom: '40px',
          }}>
            "Creating was never optional. It was survival.<br />
            VVS is the structure I wish had existed when I started //<br />
            built so no artist ever has to choose between their vision and the world."
          </p>
          <p style={{
            fontFamily: 'Syne, sans-serif',
            fontSize: '14px',
            fontWeight: 600,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: 'var(--vvs-cyan)',
          }}>
            fromnow // Founder & Artist, Vague Vision Sound
          </p>
        </div>
      </section>

      {/* Scrolling Text */}
      <ScrollingText text="FROM BRUSSELS WITH LOVE //" speed={45} />

      {/* ═══════════════════════════════════════════════════════════════════════
          COLLABORATIONS & ECOSYSTEM
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{ 
        padding: isMobile ? '80px 20px' : '120px 48px',
      }}>
        <div ref={collabRef} className="reveal-el" style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? '28px' : 'clamp(36px, 5vw, 52px)',
            lineHeight: 1.0,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '24px',
          }}>
            COLLABORATIONS & ECOSYSTEM
          </h2>
          
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '15px' : '16px',
            color: 'var(--vvs-muted)',
            lineHeight: 1.8,
            marginBottom: '48px',
            maxWidth: '700px',
          }}>
            VVS doesn't operate alone. We are part of a broader ecosystem of artists, collectives, and structures that share the same values.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px',
          }}>
            <CollabCard 
              name="Urban Echoes"
              description="Brussels techno collective founded by Bored Bouddha and fromnow. Raw venues, international podcast series, events that make spaces breathe differently."
              link="https://urbanechoes.org"
              linkLabel="urbanechoes.org"
            />
            <CollabCard 
              name="Metro Club"
              description="The first event collaborator of VVS. A venue that believed in the vision before anyone else // and gave VVS its founding night on June 27, 2025."
              link="https://instagram.com/metroclub.ath"
              linkLabel="@metroclub.ath"
            />
            <CollabCard 
              name="Momento"
              description="An international initiative sharing the same core values as VVS // creating spaces where connection and culture transcend geography. More than a partner, a mirror of what we believe in."
            />
            <CollabCard 
              name="Musicpreneurs"
              description="Co-founded by Nina alongside Piotr Paluch, fromnow and Denis Bogaerts. A structure built to help artists understand their own ecosystem // artistic, financial and legal. Because knowing how the system works is the first step to owning your place in it."
            />
            <CollabCard 
              name="Temple Studios"
              description="Professional recording studio, close collaborator for production and mixing sessions."
            />
            <CollabCard 
              name="DISOSS"
              description="Swiss dub techno label, released fromnow's Strong Swing High."
            />
            <CollabCard 
              name="el3ment"
              description="Invite-only global artist network based in Los Angeles. VVS artists integrated."
            />
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          CONTACT
      ═══════════════════════════════════════════════════════════════════════ */}
      <section style={{ 
        padding: isMobile ? '80px 20px 100px' : '120px 48px 140px',
        background: 'rgba(10,22,40,0.3)',
        borderTop: '1px solid rgba(240,244,248,0.04)',
      }}>
        <div ref={contactRef} className="reveal-el" style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 800,
            fontSize: isMobile ? '28px' : 'clamp(36px, 5vw, 52px)',
            lineHeight: 1.0,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '40px',
          }}>
            CONTACT
          </h2>
          
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: isMobile ? '16px' : '18px',
            color: 'var(--vvs-muted)',
            lineHeight: 1.9,
            marginBottom: '48px',
          }}>
            You make music.<br />
            You want to work together.<br />
            You have a project that needs a home.
          </p>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            alignItems: 'center',
            marginBottom: '48px',
          }}>
            {[
              { label: 'Demos', email: 'demos@vaguevisionsound.com' },
              { label: 'Partnerships', email: 'partnerships@vaguevisionsound.com' },
              { label: 'General', email: 'contact@vaguevisionsound.com' },
            ].map(item => (
              <a 
                key={item.email}
                href={`mailto:${item.email}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  fontFamily: 'Space Grotesk, sans-serif',
                  fontSize: '15px',
                  color: 'var(--vvs-muted)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--vvs-cyan)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--vvs-muted)'}
              >
                <span style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'var(--vvs-cyan)',
                  minWidth: '120px',
                  textAlign: 'right',
                }}>
                  {item.label} :
                </span>
                {item.email}
              </a>
            ))}
          </div>

          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: '15px',
            color: 'var(--vvs-muted)',
            marginBottom: '28px',
          }}>
            Or use the contact form
          </p>

          <Link 
            to="/contact"
            data-testid="vision-contact-btn"
            className="btn-metallic btn-metallic-filled"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              textDecoration: 'none',
              fontSize: '13px',
              letterSpacing: '0.1em',
            }}
          >
            Contact <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Footer Scrolling Text */}
      <ScrollingText text="FROM BRUSSELS WITH LOVE //" speed={50} />

    </div>
  );
}
